import {
  faBuilding, faFile, faHome, faPage, faTruck, faUser,
} from '@fortawesome/pro-light-svg-icons'

import { DateTime } from 'luxon'

import { digObject, generateRedirectUrl, sortArrayBy } from '@domain-group/javascript-utils'

import { baseURL } from '@functions/api'
import { getRealhubLoginParams } from '@functions/entity'
import searchEndpoint from '@functions/search'
import { resultStrings } from '@functions/dashboardSearch'
import { dashboardSearchOptions } from '@functions/navigation'

import { generateUrls as generateAgencyUrls } from '@hooks/useAgency'
import { generateUrls as generateArtworkUrls } from '@hooks/useArtwork'
import { generateUrls as generateCampaignUrls } from '@hooks/useCampaign'
import { generateUrls as generateCampaignQuoteTemplateUrls } from '@hooks/useCampaignQuoteTemplate'
import { generateUrls as generateCampaignQuoteUrls } from '@hooks/useCampaignQuote'
import useCurrentUser, { CurrentUserPayload } from '@hooks/useCurrentUser'
import useEntityImpersonation, { UseEntityImpersonationPayload } from '@hooks/useEntityImpersonation'
import { generateUrls as generateInvoiceUrls } from '@hooks/useInvoice'
import { generateUrls as generateOrderUrls } from '@hooks/useOrder'
import { generateUrls as generateProviderUrls } from '@hooks/useProvider'
import { generateUrls as generateUserUrls } from '@hooks/useUser'

type ResultStrings = typeof resultStrings

export type Result = {
  agency?: {
    id: number,
    realhub_platform: 'legacy' | 'standalone_v1',
    title: string,
  },
  agency_id?: number,
  artwork_id?: number,
  brand?: {
    id: number,
    realhub_platform: 'legacy' | 'standalone_v1',
    title: string,
  },
  brand_template?: boolean,
  campaign?: {
    created_at?: string,
    hidden: boolean,
    id: number,
    published: boolean,
    title: string,
  },
  campaign_id?: number,
  login_as: {
    user_id: string,
  },
  id: number,
  order_id?: number,
  portal_title?: string,
  provider?: {
    id: number,
    realhub_platform: 'legacy' | 'standalone_v1',
    title: string,
  },
  title?: string,
  type: keyof ResultStrings,
  url: string,
  user?: {
    full_name: number,
    role_title: string,
  },
}

type CommonParams = {
  callbacks: {
    generateTokenAndImpersonate: UseEntityImpersonationPayload['callbacks']['generateTokenAndImpersonate'],
  },
  currentUserPayload: CurrentUserPayload,
  result: Result,
}

const shouldUseNewFrontend = (entity: Result['agency' | 'brand' | 'provider']) => (
  entity?.realhub_platform === 'standalone_v1'
)

const buildLegacyLogin = (result: Result) => {
  const { login_as, url } = result

  return { shouldUseLegacyLogin: true, loginAsUserParams: { redirect: url, userId: login_as?.user_id } }
}

type BuildAdminLinksParams = {
  callbacks: CommonParams['callbacks'],
  redirectPath?: string,
  result: Result,
}

const buildAdminLinks = (params: BuildAdminLinksParams) => {
  const { callbacks: { generateTokenAndImpersonate }, redirectPath = '/', result } = params

  const {
    agency, brand, provider, user,
  } = result

  const association = agency || brand || provider
  const loginAs = user || association

  if (shouldUseNewFrontend(association)){
    // @ts-expect-error TS2322
    return { onClick: () => generateTokenAndImpersonate({ entity: loginAs, redirectPath }) }
  }

  return buildLegacyLogin(result)
}

const buildAgencyLinks = (params: CommonParams) => {
  const {
    callbacks,
    currentUserPayload,
    result,
    result: { agency },
  } = params

  const { isAdmin, isBrandUser, isProviderUser } = currentUserPayload

  const { editAgencyRelationsUrl, editProviderAccountUrl } = generateAgencyUrls(agency)

  if (isAdmin || isBrandUser){
    const loginAction = buildAdminLinks({ callbacks, result })

    return {
      actions: [
        { title: 'Login as Agency', ...loginAction },
        { title: 'Edit Agency', href: `${editAgencyRelationsUrl}?redirect=${generateRedirectUrl()}` },
      ],
      ...loginAction,
    }
  }

  if (isProviderUser) return { href: editProviderAccountUrl }

  return {}
}

const buildArtworkLinks = (params: CommonParams) => {
  const {
    callbacks: { generateTokenAndImpersonate },
    currentUserPayload,
    result,
    result: { agency },
  } = params

  const { isAdmin, isAgencyUser } = currentUserPayload

  const { editArtworkUrl } = generateArtworkUrls(result, { includeRedirect: true })

  if (isAdmin){
    return shouldUseNewFrontend(agency)
      // @ts-expect-error TS2322
      ? { onClick: () => generateTokenAndImpersonate({ entity: agency, redirectPath: editArtworkUrl }) }
      : buildLegacyLogin(result)
  }

  if (isAgencyUser) return { href: editArtworkUrl }

  return {}
}

const buildCampaignLinks = (params: CommonParams) => {
  const {
    callbacks, callbacks: { generateTokenAndImpersonate }, currentUserPayload, result,
  } = params

  const {
    isAdmin, isAgencyUser, isBrandUser, isProviderUser,
  } = currentUserPayload

  const { agency, campaign } = result
  const { campaignEditDashboardUrl, editCampaignUrl } = generateCampaignUrls(campaign)

  if (isAdmin) return buildAdminLinks({ callbacks, redirectPath: campaignEditDashboardUrl, result })
  if (isAgencyUser) return { href: campaignEditDashboardUrl }
  if (isBrandUser){
    return shouldUseNewFrontend(agency)
      // @ts-expect-error TS2322
      ? { onClick: () => generateTokenAndImpersonate({ entity: agency, redirectPath: campaignEditDashboardUrl }) }
      : buildLegacyLogin(result)
  }
  if (isProviderUser) return { href: editCampaignUrl }

  return {}
}

const buildCampaignQuoteLinks = (params: CommonParams) => {
  const { currentUserPayload, result } = params
  const { isAgencyUser } = currentUserPayload

  const prefixRoute = `campaigns/${result.campaign_id}/edit`
  const { editCampaignQuoteUrl } = generateCampaignQuoteUrls(result, { prefixRoute })

  if (isAgencyUser) return { href: editCampaignQuoteUrl }

  return {}
}

const buildCampaignQuoteTemplateLinks = (params: CommonParams) => {
  const { currentUserPayload, result } = params
  const { isAgencyUser } = currentUserPayload

  const prefixRoute = `agencies/${result.agency_id}/manage`
  const { editCampaignQuoteTemplateUrl } = generateCampaignQuoteTemplateUrls(result, { prefixRoute })

  if (isAgencyUser) return { href: editCampaignQuoteTemplateUrl }

  return {}
}

const buildInvoiceLinks = (params: CommonParams) => {
  const { currentUserPayload, result } = params
  const { isAgencyUser } = currentUserPayload

  const prefixRoute = `campaigns/${result.campaign_id}/edit`
  // @ts-expect-error TS2345
  const { editCampaignInvoiceUrl } = generateInvoiceUrls(result, { prefixRoute })

  if (isAgencyUser) return { href: editCampaignInvoiceUrl }

  return {}
}

const buildOrderLinks = (params: CommonParams) => {
  const { callbacks, currentUserPayload, result } = params

  const {
    isAdmin, isBrandUser, isProviderUser, isAgencyUser,
  } = currentUserPayload

  const { editOrderUrl, pendingCampaignOrdersUrl } = generateOrderUrls(result)

  if (isAdmin) return buildAdminLinks({ callbacks, redirectPath: pendingCampaignOrdersUrl, result })
  if (isAgencyUser) return { href: pendingCampaignOrdersUrl }
  if (isBrandUser || isProviderUser) return { href: editOrderUrl }

  return {}
}

const buildProviderLinks = (params: CommonParams) => {
  const {
    callbacks,
    currentUserPayload,
    result,
    result: { provider },
  } = params

  const { isAdmin } = currentUserPayload

  const { editProviderRelationsUrl } = generateProviderUrls(provider)
  const { orderIndexUrl } = generateOrderUrls()

  if (isAdmin){
    const loginAction = buildAdminLinks({ callbacks, redirectPath: orderIndexUrl, result })

    return {
      actions: [
        { title: 'Login as Provider', ...loginAction },
        { title: 'Edit Provider', href: `${editProviderRelationsUrl}?redirect=${generateRedirectUrl()}` },
      ],
      ...loginAction,
    }
  }

  return {}
}

const buildUserLinks = (params: CommonParams) => {
  const {
    callbacks,
    currentUserPayload,
    result,
    result: { agency },
  } = params

  const { isAdmin, isAgencyUser } = currentUserPayload

  const prefixRoute = isAgencyUser ? `agencies/${agency?.id}/manage` : undefined
  // @ts-expect-error TS2345
  const { editUserUrl } = generateUserUrls(result, { prefixRoute })

  if (isAdmin){
    const loginAction = buildAdminLinks({ callbacks, result })

    return {
      actions: [
        { title: 'Login as User', ...loginAction },
        { title: 'Edit User', href: `${editUserUrl}?redirect=${generateRedirectUrl()}` },
      ],
      ...loginAction,
    }
  }

  if (isAgencyUser) return { href: editUserUrl }

  return {}
}

const buildLinks = (params: CommonParams) => {
  const { result, result: { type } } = params

  if (type === 'Artwork') return buildArtworkLinks(params)
  // @ts-expect-error TS2322
  if (type === 'ArtworkPage') return buildArtworkLinks({ ...params, result: { ...result, id: result.artwork_id } })
  if (type === 'CampaignQuote') return buildCampaignQuoteLinks(params)
  if (type === 'Invoice') return buildInvoiceLinks(params)
  if (type === 'Order') return buildOrderLinks(params)
  // @ts-expect-error TS2322
  if (type === 'OrderItem') return buildOrderLinks({ ...params, result: { ...result, id: result.order_id } })

  // For templates, digital pages, proposals
  return buildLegacyLogin(result)
}

const formatResult = (params: CommonParams) => {
  const { result } = params

  const {
    agency, brand, brand_template, campaign, id, portal_title, provider, title, type, user,
  } = result

  if (type === 'Agency'){
    return {
      icon: faBuilding,
      subtitle: `#${id}`,
      title: agency?.title,
      ...buildAgencyLinks(params),
    }
  }

  if (type === 'Campaign'){
    const hiddenText = campaign?.hidden ? 'Archived' : 'Current'
    const publishedText = campaign?.published ? 'Published' : 'Not Published'
    const createdDate = campaign?.created_at
      ? DateTime.fromFormat(campaign.created_at, 'dd/MM/yyyy').toLocaleString(DateTime.DATE_MED) : ''

    return {
      icon: faHome,
      subtitle: `${agency?.title}${createdDate ? ` | ${createdDate}` : ''}`,
      tertiaryText: `${portal_title} | ${publishedText} | ${hiddenText}`,
      title: campaign?.title,
      ...buildCampaignLinks(params),
    }
  }

  if (type === 'CampaignQuoteTemplate'){
    return {
      icon: faPage,
      subtitle: `Quote Template #${id}`,
      title,
      ...buildCampaignQuoteTemplateLinks(params),
    }
  }

  if (type === 'Provider'){
    return {
      icon: faTruck,
      subtitle: `#${id}`,
      title: provider?.title,
      ...buildProviderLinks(params),
    }
  }

  if (type === 'User'){
    return {
      icon: faUser,
      subtitle: user?.role_title,
      tertiaryText: agency?.title || brand?.title || provider?.title,
      title: user?.full_name,
      ...buildUserLinks(params),
    }
  }

  return {
    icon: faFile,
    subtitle: campaign?.title,
    tertiaryText: brand_template ? 'BrandTemplate' : agency?.title,
    title: title || `${type} #${id}`,
    ...buildLinks(params),
  }
}

export type FormattedResult = ReturnType<typeof formatResult>

type GroupedResults = {
  [key: ResultStrings[keyof ResultStrings]['key']]: { items: ReturnType<typeof formatResult>[], title: string },
}

type SortResultsParams = {
  callbacks: {
    generateTokenAndImpersonate: UseEntityImpersonationPayload['callbacks']['generateTokenAndImpersonate'],
  },
  currentUserPayload: CurrentUserPayload,
  results: Result[],
}

const sortResults = (params: SortResultsParams) => {
  const { callbacks, currentUserPayload, results = [] } = params

  const groupedResults = results.reduce((grouped: GroupedResults, result) => {
    const { type } = result
    const { key, title } = resultStrings[type]

    grouped[key] = grouped[key] || { items: [], title }

    const formattedResult = formatResult({ callbacks, currentUserPayload, result })
    grouped[key].items.push(formattedResult)

    return grouped
  }, {})

  return sortArrayBy(Object.values(groupedResults))
}

type SearchOptions = {
  string?: string,
}

type DashboardSearchParams = {
  options: SearchOptions,
  searchEntitiesByDetail: string[],
  searchEntitiesById: string[],
}

const dashboardSearch = (params: DashboardSearchParams) => {
  const { options, searchEntitiesByDetail, searchEntitiesById } = params
  const string = digObject(options, 'string', '')

  if (string.length > 0){
    const searchParams = {
      ...getRealhubLoginParams(),
      include_hidden_campaigns: true,
      search_entities_by_detail: searchEntitiesByDetail,
      search_entities_by_id: searchEntitiesById,
    }

    return searchEndpoint(string, searchParams, `${baseURL}/dashboard/search.json`)
  }
  return Promise.resolve({ success: false })
}

type MapResultsParams = {
  callbacks: {
    generateTokenAndImpersonate: UseEntityImpersonationPayload['callbacks']['generateTokenAndImpersonate'],
  },
  currentUserPayload: CurrentUserPayload,
  results: Result[],
}

const mapResults = (params: MapResultsParams) => {
  const { callbacks, currentUserPayload, results = [] } = params

  const sortedResults = sortResults({ callbacks, currentUserPayload, results })

  const emptyResult = [{
    items: [{
      id: -1,
      key: -1,
      subtitle: 'Try searching again',
      title: 'No Entities found',
      priority: 0,
    }],
    title: 'Results',
  }]

  const groups = results.length ? sortedResults : emptyResult

  return { groups }
}

const useDashboardSearch = () => {
  const currentUserPayload = useCurrentUser()
  const { audience } = currentUserPayload

  // @ts-expect-error TS7053
  const { searchEntitiesByDetail, searchEntitiesById } = dashboardSearchOptions[audience] || {}

  const { callbacks: { generateTokenAndImpersonate } } = useEntityImpersonation()

  return {
    callbacks: {
      dashboardSearch: (options: SearchOptions) => (
        dashboardSearch({ options, searchEntitiesByDetail, searchEntitiesById })
      ),
      mapResults: (data: Result[]) => (
        mapResults({ callbacks: { generateTokenAndImpersonate }, currentUserPayload, results: data })
      ),
    },
  }
}

export default useDashboardSearch
